import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getScholars,
  approveScholar,
  rejectScholar,
  deleteScholar,
  getScholarProfile,
  Scholar,
  ScholarListResult,
  suspendScholar,
  unsuspendScholar,
} from "../services/adminService";
import { cacheRead, cacheWrite, cacheInvalidate, cacheAge } from "../utils/dataCache";
import {
  pageContainer, headerStyle, breadcrumb, titleStyle, subtitleStyle,
  statsGrid, cardStyle, labelStyle, valueStyle, sectionTitle,
  primaryBtn, secondaryBtn, tableSearch, selectStyle, thStyle, tdStyle, badgeStyle,
} from "../styles/theme";
import ProfileModal from "../components/ProfileModal";
import ScholarApprovalModal from "../components/ScholarApprovalModal";
import ReasonModal from "../components/ReasonModal";
import { formatScholarId } from "../utils/idFormatter";
import { exportToCSV } from "../utils/csvExport";
import { getUserRole, getUserDepartment } from "../utils/auth";

// ── Cache config ───────────────────────────────────────────────
const CACHE_NS      = 'scholars:';
const HARD_TTL_MS   = 5 * 60 * 1000; // 5 min
const REVALIDATE_MS = 60 * 1000;      // 60 s — background refresh threshold

// ── Debounce hook ─────────────────────────────────────────────
function useDebounce<T>(value: T, delayMs: number): T {
  const [dv, setDv] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return dv;
}

// ── Pagination ────────────────────────────────────────────────
const PAGE_SIZE = 10;

interface PaginatorProps { total: number; page: number; onPage: (p: number) => void; }
const Paginator: React.FC<PaginatorProps> = ({ total, page, onPage }) => {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(pages, page + 2);
  const nums: number[] = [];
  for (let i = start; i <= end; i++) nums.push(i);

  const btn = (label: string | number, target: number, active = false, disabled = false) => (
    <button key={label} onClick={() => !disabled && onPage(target)} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
      background: active ? "#2563eb" : disabled ? "#f8fafc" : "#fff",
      color: active ? "#fff" : disabled ? "#cbd5e1" : "#475569",
      fontWeight: active ? 700 : 500, fontSize: 13,
      cursor: disabled ? "default" : "pointer", transition: "all .15s",
    }}>{label}</button>
  );

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", marginTop: 16 }}>
      <span style={{ fontSize: 12, color: "#94a3b8", marginRight: 8 }}>
        Page {page} of {pages} ({total} results)
      </span>
      {btn("‹", page - 1, false, page === 1)}
      {start > 1 && <>{btn(1, 1)}{start > 2 && <span style={{ color: "#94a3b8" }}>…</span>}</>}
      {nums.map((n) => btn(n, n, n === page))}
      {end < pages && <>{end < pages - 1 && <span style={{ color: "#94a3b8" }}>…</span>}{btn(pages, pages)}</>}
      {btn("›", page + 1, false, page === pages)}
    </div>
  );
};

// ── Status styles ─────────────────────────────────────────────
const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  APPROVED:       { bg: "#dcfce7", fg: "#15803d" },
  PENDING:        { bg: "#fff7ed", fg: "#c2410c" },
  PENDING_REVIEW: { bg: "#fff7ed", fg: "#c2410c" },
  REJECTED:       { bg: "#fee2e2", fg: "#b91c1c" },
  SUSPENDED:      { bg: "#fef3c7", fg: "#92400e" },
};

function formatAge(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  return `${Math.round(ms / 60_000)}m ago`;
}

// ── Section table ─────────────────────────────────────────────
interface SectionProps {
  title: string;
  badge?: React.ReactNode;
  rows: Scholar[];
  loading: boolean;
  actionLoading: string | null;
  onApprove: (id: string, name: string) => void;
  onReject:  (id: string, name: string) => void;
  onToggleSuspend: (id: string, currentlySuspended: boolean, name: string) => void;
  onDelete:  (id: string, name: string) => void;
  onView:    (id: string) => void;
  emptyMsg: string;
  showActions: boolean;
}

const SectionTable: React.FC<SectionProps> = ({
  title, badge, rows, loading, actionLoading,
  onApprove, onReject, onToggleSuspend, onDelete, onView, emptyMsg, showActions,
}) => {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows.length]);
  const paginated = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const role = getUserRole();
  const department = getUserDepartment();
  const isSupport = role === "SUPPORT" || department === "SUPPORT";

  return (
    <div style={{ ...cardStyle, marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h3 style={{ ...sectionTitle, margin: 0 }}>{title}</h3>
          {badge}
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{rows.length} scholars</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <th style={thStyle}>SCHOLAR</th>
              <th style={thStyle}>CONTACT</th>
              <th style={thStyle}>RELIGION</th>
              <th style={thStyle}>STATUS</th>
              <th style={{ ...thStyle, textAlign: "right" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading…</td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>{emptyMsg}</td></tr>
            ) : paginated.map((a) => {
              const status = a.status || "PENDING_REVIEW";
              const sc = STATUS_STYLE[status] || { bg: "#f1f5f9", fg: "#64748b" };
              const isPending = ["PENDING", "PENDING_REVIEW"].includes(status);

              return (
                <tr
                  key={a.id}
                  style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer", transition: "background .15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "hsl(var(--muted) / 0.6)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={() => onView(a.id)}
                >
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, color: "#fff", fontSize: 14, flexShrink: 0,
                      }}>
                        {(a.user?.name || "U").charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: "hsl(var(--foreground))", fontSize: 14 }}>{a.user?.name || "Unknown"}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#2563eb", background: "#eff6ff", padding: "1px 6px", borderRadius: 4 }}>
                            {formatScholarId(a.id)}
                          </span>
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>· {(a as any).onboardingStep || "–"}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: 13, color: "hsl(var(--foreground))" }}>{a.user?.email || "–"}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{a.user?.phone || "No phone"}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", padding: "3px 8px", borderRadius: 6 }}>
                      {a.religion}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: sc.bg, color: sc.fg, fontSize: 11 }}>
                      {status.replace("_", " ")}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {!isSupport && showActions && isPending && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); onApprove(a.id, a.user?.name || "this scholar"); }}
                            disabled={actionLoading === a.id}
                            style={{
                              padding: "5px 12px", borderRadius: 6, border: "none",
                              background: actionLoading === a.id ? "#d1fae5" : "#10b981",
                              color: "#fff", fontWeight: 700, fontSize: 12,
                              cursor: actionLoading === a.id ? "not-allowed" : "pointer",
                            }}
                          >
                            {actionLoading === a.id ? "…" : "Approve"}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onReject(a.id, a.user?.name || "this scholar"); }}
                            disabled={actionLoading === a.id}
                            style={{
                              padding: "5px 12px", borderRadius: 6, border: "1px solid #fecaca",
                              background: "#fff", color: "#dc2626", fontWeight: 700, fontSize: 12,
                              cursor: actionLoading === a.id ? "not-allowed" : "pointer",
                            }}
                          >
                            ✕ Reject
                          </button>
                        </>
                      )}
                      {!isSupport && (status === "APPROVED" || status === "SUSPENDED") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onToggleSuspend(a.id, status === "SUSPENDED", a.user?.name || "this scholar"); }}
                          disabled={actionLoading === a.id}
                          style={{
                            padding: "5px 12px", borderRadius: 6, border: "none",
                            background: status !== "SUSPENDED" ? "#fee2e2" : "#dcfce7",
                            color:      status !== "SUSPENDED" ? "#dc2626" : "#15803d",
                            fontWeight: 700, fontSize: 12,
                            cursor: actionLoading === a.id ? "not-allowed" : "pointer",
                          }}
                        >
                          {status !== "SUSPENDED" ? "Suspend" : "Activate"}
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onView(a.id); }}
                        style={{
                          padding: "5px 12px", borderRadius: 6, border: "none",
                          background: "#eff6ff", color: "#2563eb",
                          fontWeight: 700, fontSize: 12, cursor: "pointer",
                        }}
                      >
                        View Profile
                      </button>
                      {!isSupport && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(a.id, a.user?.name || "this scholar"); }}
                          disabled={actionLoading === a.id}
                          title="Delete scholar account"
                          style={{
                            padding: "5px 10px", borderRadius: 6, border: "1px solid #fecaca",
                            background: "#fff5f5", color: "#dc2626",
                            fontWeight: 700, fontSize: 12,
                            cursor: actionLoading === a.id ? "not-allowed" : "pointer",
                          }}
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Paginator total={rows.length} page={page} onPage={setPage} />
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────
const EMPTY_RESULT: ScholarListResult = {
  items: [], summary: { total: 0, pending: 0, approved: 0, rejected: 0 },
};

const ScholarDashboard = () => {
  const [result, setResult]               = useState<ScholarListResult>(EMPTY_RESULT);
  const [loading, setLoading]             = useState(true);
  const [revalidating, setRevalidating]   = useState(false);
  const [lastSyncedAt, setLastSyncedAt]   = useState<number | null>(null);
  const [syncLabel, setSyncLabel]         = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast]                 = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [profileOpen, setProfileOpen]     = useState(false);
  const [profileData, setProfileData]     = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalTarget, setApprovalTarget]       = useState<{ id: string; name: string } | null>(null);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget]       = useState<{ id: string; name: string } | null>(null);

  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendTarget, setSuspendTarget]       = useState<{ id: string; name: string } | null>(null);

  const [deleteModalOpen, setDeleteModalOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget]         = useState<{ id: string; name: string } | null>(null);

  const [searchInput,    setSearchInput]    = useState("");
  const [religionFilter, setReligionFilter] = useState("");
  const [processing, setProcessing]         = useState(false);
  const debouncedSearch = useDebounce(searchInput, 350);

  // Tick "synced X ago"
  useEffect(() => {
    if (!lastSyncedAt) return;
    const tick = () => setSyncLabel(formatAge(Date.now() - lastSyncedAt));
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  // ── Core fetch ────────────────────────────────────────────
  const doFetch = useCallback(async (silent = false) => {
    const filters = {
      search:   debouncedSearch  || undefined,
      religion: religionFilter   || undefined,
    };
    const key = `${CACHE_NS}${debouncedSearch}:${religionFilter}`;

    if (!silent) setLoading(true);
    else setRevalidating(true);

    const data = await getScholars(filters);
    cacheWrite(key, data);
    setResult(data);
    setLastSyncedAt(Date.now());
    setSyncLabel('just now');

    if (!silent) setLoading(false);
    else setRevalidating(false);
  }, [debouncedSearch, religionFilter]);

  // ── On filter change or mount: use cache if available ────
  const fetchScholars = useCallback(async () => {
    const key = `${CACHE_NS}${debouncedSearch}:${religionFilter}`;
    const cached = cacheRead<ScholarListResult>(key, HARD_TTL_MS);

    if (cached) {
      setResult(cached);
      setLoading(false);
      const age = cacheAge(key) ?? 0;
      setLastSyncedAt(Date.now() - age);
      if (age > REVALIDATE_MS) doFetch(true);
      return;
    }

    doFetch(false);
  }, [debouncedSearch, religionFilter, doFetch]);

  useEffect(() => { fetchScholars(); }, [fetchScholars]);

  // ── 30-second auto-poll — refreshes silently in background ──
  useEffect(() => {
    const id = setInterval(() => {
      doFetch(true); // silent = doesn't show full-page spinner
    }, 30_000);
    return () => clearInterval(id);
  }, [doFetch]);

  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Mutations: bust cache, hard reload ────────────────────
  const handleOpenApproveModal = (id: string, name: string) => {
    setApprovalTarget({ id, name });
    setApprovalModalOpen(true);
  };

  const handleApproveConfirm = async (id: string, name: string) => {
    setActionLoading(id);
    try {
      await approveScholar(id);
      showToast(`${name} approved successfully.`, "success");
      setApprovalModalOpen(false);
      cacheInvalidate(CACHE_NS);
      await doFetch(false);
    } catch {
      showToast(`Failed to approve ${name}.`, "error");
    } finally { 
      setActionLoading(null);
      setApprovalTarget(null);
    }
  };

  const handleReject = (id: string, name: string) => {
    setRejectTarget({ id, name });
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectTarget) return;
    const { id, name } = rejectTarget;
    setActionLoading(id);
    try {
      await rejectScholar(id, reason);
      showToast(`Scholar "${name}" rejected.`, "success");
      setRejectModalOpen(false);
      cacheInvalidate(CACHE_NS);
      await doFetch(false);
    } catch {
      showToast(`Failed to reject ${name}.`, "error");
    } finally { 
      setActionLoading(null);
      setRejectTarget(null);
    }
  };

  const handleViewProfile = async (scholarId: string) => {
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    const data = await getScholarProfile(scholarId);
    setProfileData(data);
    setProfileLoading(false);
  };

  const handleToggleSuspend = async (scholarId: string, currentlySuspended: boolean, name: string) => {
    if (currentlySuspended) {
      if (!window.confirm(`Are you sure you want to activate scholar "${name}"?`)) return;
      setActionLoading(scholarId);
      try {
        await unsuspendScholar(scholarId);
        showToast(`${name} activated successfully.`, "success");
        cacheInvalidate(CACHE_NS);
        await doFetch(false);
      } catch {
        showToast(`Failed to activate ${name}.`, "error");
      } finally {
        setActionLoading(null);
      }
    } else {
      setSuspendTarget({ id: scholarId, name });
      setSuspendModalOpen(true);
    }
  };

  const handleSuspendConfirm = async (reason: string) => {
    if (!suspendTarget) return;
    const { id, name } = suspendTarget;
    setSuspendModalOpen(false);
    setActionLoading(id);
    try {
      await suspendScholar(id, reason);
      showToast(`${name} suspended successfully.`, "success");
      cacheInvalidate(CACHE_NS);
      await doFetch(false);
    } catch {
      showToast(`Failed to suspend ${name}.`, "error");
    } finally {
      setActionLoading(null);
      setSuspendTarget(null);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    setActionLoading(id);
    try {
      await deleteScholar(id);
      showToast(`Scholar "${name}" deleted permanently.`, "success");
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      cacheInvalidate(CACHE_NS);
      await doFetch(false);
    } catch (err: any) {
      showToast(err?.response?.data?.message || `Failed to delete ${name}.`, "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportCSV = async () => {
    setProcessing(true);
    try {
      const exportData = result.items.map(s => ({
        Name: s.user?.name || 'N/A',
        Email: s.user?.email || 'N/A',
        Phone: s.user?.phone || 'N/A',
        Religion: s.religion,
        Status: s.status,
        Rating: s.rating || 0,
        TotalReviews: s.totalReviews || 0,
        OnboardingStep: (s as any).onboardingStep || 'N/A',
        CreatedAt: new Date(s.createdAt).toLocaleDateString(),
      }));
      exportToCSV(exportData, 'Scholars_Report');
    } finally {
      setProcessing(false);
    }
  };

  const { items, summary } = result;

  const pendingRows = useMemo(
    () => items.filter((a) => ["PENDING", "PENDING_REVIEW"].includes(a.status || "")),
    [items],
  );

  const stats = [
    { label: "Total",          val: summary.total.toString(),    color: "#2563eb" },
    { label: "Pending Review", val: summary.pending.toString(),  color: "#f59e0b" },
    { label: "Approved",       val: summary.approved.toString(), color: "#10b981" },
    { label: "Rejected",       val: summary.rejected.toString(), color: "#ef4444" },
  ];

  return (
    <div style={pageContainer}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 9999,
          padding: "14px 20px", borderRadius: 12,
          background: toast.type === "success" ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${toast.type === "success" ? "#bbf7d0" : "#fecaca"}`,
          color: toast.type === "success" ? "#166534" : "#b91c1c",
          fontSize: 14, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", maxWidth: 360,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <div style={breadcrumb}>Dashboard {">"} Scholars</div>
          <h1 style={titleStyle}>Scholar Management</h1>
          <p style={subtitleStyle}>Review applications, manage approvals, and monitor scholar activity.</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {lastSyncedAt && !loading && (
            <span style={{ fontSize: 11, color: revalidating ? "#f59e0b" : "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}>
              {revalidating ? 'Syncing…' : `Synced ${syncLabel}`}
            </span>
          )}
          <button style={{ ...secondaryBtn, opacity: processing ? 0.6 : 1, cursor: processing ? 'not-allowed' : 'pointer' }} onClick={handleExportCSV} disabled={processing}>
            {processing ? '...' : 'Export CSV'}
          </button>
          <button
            style={{ ...primaryBtn, opacity: processing || loading ? 0.6 : 1, cursor: processing || loading ? 'not-allowed' : 'pointer' }}
            onClick={async () => { 
              if (processing || loading) return;
              setProcessing(true);
              cacheInvalidate(CACHE_NS); 
              await doFetch(false);
              setProcessing(false);
            }}
            disabled={processing || loading}
          >
            {processing ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={statsGrid}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ ...labelStyle, color: "#64748b" }}>{s.label}</div>
            <div style={{ ...valueStyle, color: s.color, margin: "8px 0" }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, padding: "14px 20px", marginBottom: 24, display: "flex", gap: 12, alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search by Scholar ID (e.g. SID1785430890), name, email, or phone…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ ...tableSearch, flex: 1 }}
        />
        <select value={religionFilter} onChange={(e) => setReligionFilter(e.target.value)} style={selectStyle}>
          <option value="">All Religions</option>
          <option value="ISLAM">Islam</option>
          <option value="HINDUISM">Hinduism</option>
          <option value="CHRISTIANITY">Christianity</option>
        </select>
        {(searchInput || religionFilter) && (
          <button
            onClick={() => { setSearchInput(""); setReligionFilter(""); }}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 12, color: "#64748b" }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Pending section */}
      <SectionTable
        title="Pending Review"
        badge={
          summary.pending > 0 ? (
            <span style={{ background: "#fff7ed", color: "#c2410c", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
              {summary.pending} need action
            </span>
          ) : undefined
        }
        rows={pendingRows}
        loading={loading}
        actionLoading={actionLoading}
        onApprove={handleOpenApproveModal}
        onReject={handleReject}
        onToggleSuspend={handleToggleSuspend}
        onDelete={handleDelete}
        onView={handleViewProfile}
        emptyMsg="No pending applications — all caught up."
        showActions={true}
      />

      {/* All scholars section */}
      <SectionTable
        title="All Scholars"
        rows={items}
        loading={loading}
        actionLoading={actionLoading}
        onApprove={handleOpenApproveModal}
        onReject={handleReject}
        onToggleSuspend={handleToggleSuspend}
        onDelete={handleDelete}
        onView={handleViewProfile}
        emptyMsg="No scholars found matching your filters."
        showActions={false}
      />

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        data={profileData}
        loading={profileLoading}
        type="scholar"
        onRejected={async () => {
          if (profileData?.id) {
            cacheInvalidate(CACHE_NS);
            await doFetch(true);
            setProfileLoading(true);
            const data = await getScholarProfile(profileData.id);
            setProfileData(data);
            setProfileLoading(false);
          }
        }}
      />

      <ScholarApprovalModal
        open={approvalModalOpen}
        scholarId={approvalTarget?.id || null}
        scholarName={approvalTarget?.name || ""}
        onClose={() => setApprovalModalOpen(false)}
        onApproveConfirm={handleApproveConfirm}
        processing={!!actionLoading}
      />

      <ReasonModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
        title="Reject Scholar Application"
        description={`Are you sure you want to reject ${rejectTarget?.name}'s application? This action cannot be undone.`}
        confirmText="Reject Application"
        placeholder="Reason for rejection (e.g. invalid documents, background check failed)..."
        processing={!!actionLoading}
      />

      <ReasonModal
        open={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        onConfirm={handleSuspendConfirm}
        title="Suspend Scholar ID"
        description={`Are you sure you want to suspend scholar ${suspendTarget?.name}? They will be locked out of the application and their status set to suspended.`}
        confirmText="Suspend Scholar"
        placeholder="Reason for suspension (e.g. invalid documents, complaints, violation of terms)..."
        processing={!!actionLoading}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && deleteTarget && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 11000,
            background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !actionLoading && setDeleteModalOpen(false)}
        >
          <div
            style={{
              background: "#ffffff", borderRadius: 16, width: "100%", maxWidth: "460px",
              padding: "24px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              display: "flex", flexDirection: "column", gap: "16px",
              border: "1px solid #fee2e2",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "#fee2e2", color: "#dc2626",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0,
              }}>
                ⚠️
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#991b1b" }}>
                  Delete Scholar Account
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#64748b" }}>
                  Scholar: <strong style={{ color: "#0f172a" }}>{deleteTarget.name}</strong> ({formatScholarId(deleteTarget.id)})
                </p>
              </div>
            </div>

            <div style={{
              background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 10,
              padding: "12px 14px", fontSize: "13px", color: "#b91c1c", lineHeight: "1.5",
            }}>
              <strong>Danger: This action cannot be reversed!</strong>
              <div style={{ marginTop: 4, color: "#7f1d1d", fontSize: "12px" }}>
                Deleting this scholar will permanently remove their profile, services, test bookings, payout records, reviews, and linked user credentials.
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "8px" }}>
              <button
                style={{
                  padding: "8px 16px", borderRadius: "8px", fontSize: "14px", fontWeight: 600,
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0",
                }}
                onClick={() => setDeleteModalOpen(false)}
                disabled={!!actionLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "8px 18px", borderRadius: "8px", fontSize: "14px", fontWeight: 700,
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  background: "#dc2626", color: "#fff", border: "none",
                  opacity: actionLoading ? 0.7 : 1,
                  display: "flex", alignItems: "center", gap: 6,
                }}
                disabled={!!actionLoading}
                onClick={handleDeleteConfirm}
              >
                {actionLoading ? "Deleting…" : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScholarDashboard;
